import { useEffect, useRef } from 'react';

const openDialogStack = [];

const getFocusableElements = (container) => Array.from(container?.querySelectorAll(
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
) || []).filter((element) => !element.hasAttribute('hidden') && element.getAttribute('aria-hidden') !== 'true');

const useModalBehavior = ({ isOpen, onClose, closeOnEscape = true }) => {
  const dialogRef = useRef(null);
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!isOpen || typeof document === 'undefined') return undefined;

    const previousFocus = document.activeElement;
    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    openDialogStack.push(dialogRef);

    const focusTimer = window.setTimeout(() => {
      const elements = getFocusableElements(dialogRef.current);
      (elements[0] || dialogRef.current)?.focus({ preventScroll: true });
    }, 0);

    const handleKeyDown = (event) => {
      if (openDialogStack[openDialogStack.length - 1] !== dialogRef) return;

      if (event.key === 'Escape' && closeOnEscape) {
        event.preventDefault();
        onCloseRef.current?.();
        return;
      }

      if (event.key !== 'Tab' || !dialogRef.current) return;

      const elements = getFocusableElements(dialogRef.current);
      if (elements.length === 0) {
        event.preventDefault();
        dialogRef.current.focus({ preventScroll: true });
        return;
      }

      const first = elements[0];
      const last = elements[elements.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener('keydown', handleKeyDown);
      const stackIndex = openDialogStack.lastIndexOf(dialogRef);
      if (stackIndex >= 0) openDialogStack.splice(stackIndex, 1);
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;

      if (
        typeof HTMLElement !== 'undefined'
        && previousFocus instanceof HTMLElement
        && document.contains(previousFocus)
      ) {
        previousFocus.focus({ preventScroll: true });
      }
    };
  }, [isOpen, closeOnEscape]);

  return dialogRef;
};

export default useModalBehavior;
